import React, { useEffect, useState } from 'react';
import { api, type AdBuyInfo, type AdPurchase, type AdPurchaseQuote, type LodgingQuote, type LodgingStay, type MarkAd, type QidUser } from '../../api';
import { getTheme, useThemeMode } from '../../theme';
import { Switch } from '../../components/ui';
import { LodgingStayPicker } from './LodgingStayPicker';
import { DEFAULT_CANCELLATION, emptyStay, stayForDates } from './lodging';
import { datesText } from '../../mark-tabs/AdsTab';
import { ACTIVE_PURCHASE, PURCHASE_UPDATED_EVENT, blockedText, isFreePurchase, kronor, purchaseStatusText, savePendingPurchase, spotsText, takePendingPurchase } from './adPurchase';

/**
 * Köp av annons (P1): kortet reserveras hos Stripe och dras först när marken godkänt jägaren.
 * Priset hämtas från API:t (räknas där). Oinloggad → köpet sparas, inloggning, och köpet fortsätter.
 * D5: jaktlag och gratis grupper (applyWithoutCard) söks utan kort – samma antagning, ingen Stripe.
 */
export function BuyAdButton({ ad, user, onLogin }: { ad: MarkAd; user: QidUser | null | undefined; onLogin?: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const enskild = ad.kind === 'enskild';
  const [date, setDate] = useState<string | null>(enskild && ad.dates.length === 1 ? ad.dates[0].start : null);
  const [info, setInfo] = useState<AdBuyInfo | null>(null);
  const [quote, setQuote] = useState<AdPurchaseQuote | null>(null);
  const [mine, setMine] = useState<AdPurchase | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [needsExam, setNeedsExam] = useState(false);
  // L1: annonsens boende kan läggas till och köps i samma betalning (reserveras med jakten).
  const lodging = ad.lodging?.active && ad.lodging.bookable ? ad.lodging : null;
  const [withStay, setWithStay] = useState(false);
  const [stay, setStay] = useState<LodgingStay | null>(null);
  const [stayQuote, setStayQuote] = useState<LodgingQuote | null>(null);
  useEffect(() => {
    if (!lodging) return;
    const d = enskild ? ad.dates.find(x => x.start === date) ?? (ad.dates.length === 1 ? ad.dates[0] : null) : null;
    setStay(emptyStay(lodging, stayForDates(d)));
  }, [lodging?.id, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let off = false;
    api.adBuyInfo(ad.id, date).then(i => { if (!off) setInfo(i); }).catch(() => {});
    if (!enskild || date) api.adPurchaseQuote(ad.id, date).then(q => { if (!off) setQuote(q); }).catch(() => { if (!off) setQuote(null); });
    else setQuote(null);
    return () => { off = true; };
  }, [ad.id, date, enskild]);

  const free = !!info?.applyWithoutCard;
  const loadMine = () => {
    if (!user) { setMine(null); return; }
    api.myAdPurchases().then(list => setMine(list.find(p => p.adId === ad.id && ACTIVE_PURCHASE.includes(p.status)) ?? null)).catch(() => {});
  };
  useEffect(loadMine, [ad.id, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  // Tillbaka från Stripe: App.tsx bekräftar köpet och säger till när statusen är klar.
  useEffect(() => {
    window.addEventListener(PURCHASE_UPDATED_EVENT, loadMine);
    return () => window.removeEventListener(PURCHASE_UPDATED_EVENT, loadMine);
  }, [ad.id, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = async (d: string | null) => {
    setErr(''); setNeedsExam(false);
    if (!user) { savePendingPurchase(ad.id, d); onLogin?.(); return; }
    if (enskild && !d) { setErr('Välj ett datum först'); return; }
    setBusy(true);
    try {
      if (free || info === null) {
        // Info inte hämtad än (t.ex. köpet fortsätter efter inloggning): fråga servern vilken väg som gäller.
        const isFree = free || !!(await api.adBuyInfo(ad.id, d).catch(() => null))?.applyWithoutCard;
        if (!isFree) { const { sessionUrl } = await api.startAdCheckout(ad.id, d, withStay ? stay : null); window.location.href = sessionUrl; return; }
        await api.applyToAd(ad.id, d);
        setBusy(false);
        loadMine();
        return;
      }
      const { sessionUrl } = await api.startAdCheckout(ad.id, d, withStay ? stay : null);
      window.location.href = sessionUrl;
    } catch (e: any) {
      if (/jägarexamensbevis/.test(e?.message ?? '') && !e?.code) { setNeedsExam(true); setBusy(false); return; }
      if (e?.code === 'needs_exam') setNeedsExam(true);
      else if (e?.code === 'login') { savePendingPurchase(ad.id, d); onLogin?.(); }
      else setErr(e?.message || 'Kunde inte starta köpet');
      if (e?.code === 'already_bought') loadMine();
      setBusy(false);
    }
  };

  // Tillbaka efter inloggning: fortsätt köpet som påbörjades.
  useEffect(() => {
    if (!user) return;
    const p = takePendingPurchase(ad.id);
    if (!p) return;
    if (p.dateStart) setDate(p.dateStart);
    void buy(p.dateStart);
  }, [user?.email, ad.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10, padding: 16, borderRadius: 14, border: `1px solid ${T.border}`, background: T.surface2 };

  if (mine) {
    const s = purchaseStatusText(mine.status, mine.decisionNote, isFreePurchase(mine));
    return (
      <div style={box} data-testid="ad-purchase-status">
        <div style={{ fontSize: 15, fontWeight: 800, color: s.tone === 'ok' ? T.success : T.text }}>{s.label}</div>
        <div style={{ fontSize: 13, color: T.textSub }}>{s.detail}</div>
        <div style={{ fontSize: 13, color: T.textSub }}>
          {isFreePurchase(mine) ? 'Ansökan utan kort' : kronor(mine.amount)}{mine.lodgingBookingId ? ' inkl. boende' : ''}{mine.dateStart ? ` · ${datesText([{ start: mine.dateStart, end: mine.dateEnd || mine.dateStart }])}` : ''}
          {mine.status === 'reserved' && mine.releaseAt ? ` · beslut senast ${new Date(Number(mine.releaseAt)).toLocaleDateString('sv-SE')}` : ''}
        </div>
      </div>
    );
  }

  const blocked = info && !info.buyable && info.buyBlockedReason !== 'date_required' ? info.buyBlockedReason : null;
  const full = enskild && date && info?.spotsLeft === 0;
  const stayBlocked = withStay && !stayQuote?.available;
  const disabled = busy || !!blocked || !!full || (enskild && !date) || stayBlocked;
  const total = quote ? quote.amount + (withStay && stayQuote ? stayQuote.amount : 0) : null;

  return (
    <div style={box}>
      {enskild && ad.dates.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ad.dates.map(d => {
            const on = d.start === date;
            return <button key={d.start} onClick={() => setDate(d.start)} aria-pressed={on} style={{ padding: '7px 12px', borderRadius: 999, border: `1px solid ${on ? T.text : T.border}`, background: on ? T.text : 'transparent', color: on ? T.bg : T.text, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{datesText([d])}</button>;
          })}
        </div>
      )}
      {enskild && !!ad.satNames?.length && <div data-testid="ad-sat-names" style={{ fontSize: 13, color: T.textSub }}>{ad.satNames.length === 1 ? 'Såt' : 'Såter'}: <b style={{ color: T.text }}>{ad.satNames.join(', ')}</b></div>}
      {!free && quote && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}><span style={{ color: T.textSub }}>{quote.description}</span><b>{kronor(quote.amount)}</b></div>}
      {info?.spotsLeft != null && <div style={{ fontSize: 13, color: T.textSub }}>{spotsText(info.spotsLeft)}</div>}
      {!free && lodging && stay && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8, borderTop: `1px solid ${T.border}` }} data-testid="ad-stay">
          <Switch checked={withStay} onChange={setWithStay} label={`Lägg till boende: ${lodging.name}`} style={{ justifyContent: 'space-between', width: '100%', color: T.text }} />
          {withStay && <>
            <LodgingStayPicker lodging={lodging} value={stay} onChange={setStay} onQuote={setStayQuote} />
            <div style={{ fontSize: 12, color: T.textSub }}>Avbokning av boendet: {lodging.cancellationPolicy || DEFAULT_CANCELLATION}</div>
          </>}
        </div>
      )}
      {withStay && total != null && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800 }} data-testid="ad-total"><span>Totalt</span><span>{kronor(total)}</span></div>}
      <button onClick={() => buy(date)} disabled={disabled} data-testid="buy-ad"
        style={{ width: '100%', background: disabled ? T.surface : T.accentFill, color: disabled ? T.textSub : T.onAccent, border: disabled ? `1px solid ${T.border}` : 'none', borderRadius: 12, padding: 15, fontSize: 16, fontWeight: 800, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit' }}>
        {busy ? (free ? 'Skickar ansökan…' : 'Skickar till betalning…') : free ? 'Ansök' : 'Köp – kortet reserveras'}
      </button>
      {blocked && <div style={{ fontSize: 13, color: T.textSub }}>{blockedText(blocked)}</div>}
      {needsExam && <div style={{ fontSize: 13, color: T.danger }}>Ladda upp ditt jägarexamensbevis under Profil → Jägarexamensbevis innan du {free ? 'ansöker' : 'köper'}. Det är det marken bedömer dig på.</div>}
      {err && <div style={{ fontSize: 13, color: T.danger }}>{err}</div>}
      {free
        ? <div style={{ fontSize: 12, color: T.textSub }}>Ingen betalning nu. Marken går igenom din profil och ansökan; blir du antagen får du ett avtal att signera och läggs in i gruppen.</div>
        : <div style={{ fontSize: 12, color: T.textSub }}>Beloppet reserveras på kortet och dras först när marken godkänt dig. Blir du inte godkänd, eller beslutar ingen inom 6 dagar, släpps reservationen och inget dras{withStay ? ' – det gäller även boendet' : ''}.</div>}
    </div>
  );
}
